<!-- 
Shows the action buttons, warnings and tabs for the invoice page.
-->
<template>
  <div>
    <our-actions-bar 
      :actions="actions" 
      :navigation-actions="navigationActions"
      @reverse="showReverseInvoiceTaskDialog = true"
      @send="showSendInvoiceNoticeTaskDialog = true"
      @reconcile="showAppropriateReconcileDialog()"
      @changeDueDate="showDueDateDialog = true"
    >
      <our-next-previous-navigator
        v-if="arrivedFromLevyCollection"
        :next-page-action="nextPageAction"
        :previous-page-action="previousPageAction"
        :nav-url="navUrl"
        module="invoices"
        class="mb-2"
      />
    </our-actions-bar>
    <our-warning-chips 
      :levy-collection-id="levyCollectionId"
      :provider-id="providerId"
      :warnings="invoice.warnings" 
    />
    <v-card class="home">
      <v-tabs slider-color="primary" show-arrows>
        <v-tab key="detail" :to="getUrl('detail')">
          Detail
        </v-tab>
        <v-tab key="pdf" :to="getUrl('pdf')">
          PDF
        </v-tab>
        <v-tab key="history" :to="getUrl('history')">
          History
        </v-tab>
      </v-tabs>
      <slot/>
    </v-card>
    <provider-task-dialog 
      v-model="showSendInvoiceNoticeTaskDialog" 
      :levy-collection-id="levyCollectionId" 
      :provider-id="providerId" 
      :task="sendInvoiceNoticeTask"
    />
    <provider-task-dialog 
      v-model="showReverseInvoiceTaskDialog" 
      :levy-collection-id="levyCollectionId" 
      :provider-id="providerId" 
      :task="reverseInvoiceTask"
    />
    <reconcile-with-sap-dialog
      v-model="showReconcileWithSapDialog"
      :invoice-id="invoiceId"
    />
    <reconcile-with-sap-dialog-for-testing
      v-model="showReconcileWithSapDialogForTesting"
      :invoice-id="invoiceId"
    />
    <change-invoice-due-date-dialog
      v-model="showDueDateDialog"
      :invoice-id="invoiceId"
    />
  </div>
</template>

<script>
import ProviderTaskDialog from "@/components/levy-collection-provider-tasks/ProviderTaskDialog.vue";
import ReconcileWithSapDialog from "@/components/invoices/ReconcileWithSapDialog.vue";
import ReconcileWithSapDialogForTesting from "@/components/invoices/ReconcileWithSapDialogForTesting.vue";
import ChangeInvoiceDueDateDialog from "@/components/invoices/ChangeInvoiceDueDateDialog.vue";
import lookupMixins from "@/mixins/lookup.mixins";
import * as claim from "@/store/claim-types";
import * as action from "@/store/invoices/action-types";
import * as lcpAction from "@/store/levy-collection-providers/action-types";

export default {
  components: {
    ProviderTaskDialog,
    ReconcileWithSapDialog,
    ReconcileWithSapDialogForTesting,
    ChangeInvoiceDueDateDialog
  },
  mixins: [lookupMixins],
  props: {
    invoiceId: {
      type: Number,
      required: true
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop == null,
      default: null
    },
    providerId: {
      validator: prop => typeof prop === "number" || prop == null,
      default: null
    },
    path: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showReverseInvoiceTaskDialog: false,
      showSendInvoiceNoticeTaskDialog: false,
      showReconcileWithSapDialog: false,
      showReconcileWithSapDialogForTesting: false,
      showDueDateDialog: false
    };
  },
  computed: {
    providerTasks() {
      return this.$store.state.levyCollectionProviders.tasks;
    },
    loaded() {
      return this.providerTasks.loaded;
    },
    invoice() {
      return this.$store.state.invoices.view.data;
    },
    reverseInvoiceTask() {
      return this.providerTasks.loaded && this.invoice.status == "Open"
        ? this.providerTasks.data.find(t => t.type == "ReverseInvoice" && t.isApplicable)
        : null;
    },
    sendInvoiceNoticeTask() {
      return this.providerTasks.loaded && this.invoice.status == "Open"
        ? this.providerTasks.data.find(t => t.type == "SendInvoiceNotice" && t.isApplicable)
        : null;
    },
    nextPageAction() {
      return action.LIST_NEXT_PAGE;
    },
    previousPageAction() {
      return action.LIST_PREVIOUS_PAGE;
    },
    actions() {
      const a = [];
      if (this.sendInvoiceNoticeTask && this.$store.getters.claims.includes(this.sendInvoiceNoticeTask.claimType)) {
        a.push({
          text: this.lookups.providerTaskTypeDescriptions["SendInvoiceNotice"],
          description: this.sendInvoiceNoticeTask.description,
          icon: "contact_mail",
          emit: "send"
        });
      }
      if (this.reverseInvoiceTask && this.$store.getters.claims.includes(this.reverseInvoiceTask.claimType)) {
        a.push({
          text: this.lookups.providerTaskTypeDescriptions["ReverseInvoice"],
          description: this.reverseInvoiceTask.description,
          icon: "backspace",
          emit: "reverse"
        });
      }
      if (this.canReconcileWithSap) {
        a.push({
          text: "Reconcile with SAP",
          description: "Update the details of this invoice using information retrieved from SAP.",
          icon: "refresh",
          emit: "reconcile"
        });
      }
      if (this.$store.getters.testingToolsEnabled && this.invoice.status == "Open") {
        a.push({
          text: "Change due date",
          description: "Changes the invoice due date, for testing purposes.",
          icon: "event",
          emit: "changeDueDate"
        });
      }
      return a;
    },
    navigationActions() {
      return [
        {
          text: "Go to levy collection provider",
          description: "Go to the levy collection provider for this invoice",
          url: `/levy-collections/${this.levyCollectionId}/providers/${this.providerId}/invoice`,
          icon: "school"
        }
      ];
    },
    arrivedFromLevyCollection() {
      return this.$route.path.includes("/levy-collections/");
    },
    urlPrefix() {
      if (this.$route.path.includes("/levy-collections/")) {
        return `/levy-collections/${this.levyCollectionId}`;
      } else if (this.$route.path.includes("/providers/")) {
        return `/providers/${this.providerId}`;
      } else {
        return "";
      }
    },
    navUrl() {
      return `${this.urlPrefix}/invoices/:id/${this.path}`;
    },
    canReconcileWithSap() {
      return this.isInvoicing && this.$store.getters.claims.includes(claim.SAP_RECONCILE);
    },
    isInvoicing() {
      return this.invoice.levyCollectionStatus == "Invoicing";
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    providerId() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      this.$store.dispatch(action.VIEW_REFRESH, this.invoiceId);
      if (this.levyCollectionId != null && this.providerId != null) {
        this.$store.dispatch(lcpAction.TASKS_REFRESH, {
          levyCollectionId: this.levyCollectionId,
          providerId: this.providerId
        });
      }
    },
    getUrl(suffix) {
      return `${this.urlPrefix}/invoices/${this.invoiceId}/${suffix}`;
    },
    showAppropriateReconcileDialog() {
      const s = this.$store.state.system.details;
      if (s.testing_tools_enabled && s.sap_using_dummy_gateway) {
        this.showReconcileWithSapDialogForTesting = true;
      } else {
        this.showReconcileWithSapDialog = true;
      }
    }
  }
};
</script>
